/**
 * Edit controls for the CRM pages (A7Y-4983), shared by the scoreboard rows,
 * the account page and the this-week view (A7Y-4984).
 *
 * Each control calls the matching `/admin/crm` write route and nothing else;
 * the API validates and stamps the acting admin. Each keeps its draft in its own
 * state, so a write the API rejects as stale reloads the record (`useCrmWrite`)
 * while the user's text stays in the form.
 *
 * An account's next date is derived from its open roster items and has no
 * editor: re-date or add a roster item to move it. Rename and the deletes are
 * super_admin only in the API, and only a super_admin is shown them.
 */
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CRM_BLOCKS, CRM_PRIORITIES, CRM_ROSTER_ITEM_KINDS, CRM_SEGMENTS, CRM_SLOT_CAP, CRM_STAGES } from '@a7y/types';
import type {
  CrmAccountDoc,
  CrmAccountListItem,
  CrmBlock,
  CrmContactDoc,
  CrmParkedEntry,
  CrmRosterItemDoc,
  CrmPriority,
  CrmRosterItemKind,
  CrmSegment,
  CrmSlotDoc,
  CrmStage,
  CrmWeekPlanDoc,
} from '@a7y/types';
import { api } from '@/lib/api';
import { crmErrorMessage, isCrmConflictError } from './crmErrors';
import { isoDate } from './accountView';
import { accountPagePath, splitAccountName } from './links';
import { rankLabel } from './scoreboard';
import { useCrmWrite } from './useCrmWrite';

export const CONTROL_CLASSES =
  'rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100';
const LABEL_CLASSES = 'flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400';
// A7Y-5210: below `sm` every control is a 44 px tap target (the phone check in `tests/e2e/helm-record-mobile.spec.ts`).
const PRIMARY_BUTTON =
  'min-h-11 sm:min-h-0 rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200';
const SECONDARY_BUTTON =
  'min-h-11 sm:min-h-0 rounded-md border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800';
const DANGER_BUTTON =
  'min-h-11 sm:min-h-0 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950';
export const LINK_BUTTON =
  'inline-flex min-h-11 items-center text-xs text-blue-600 hover:underline sm:min-h-0 dark:text-blue-400';

/** Stages a stage change can set: `closed` goes through Close, which records the reason. */
const SETTABLE_STAGES = CRM_STAGES.filter((s) => s !== 'closed');

/** `null` for blank text, the trimmed text otherwise. */
function orNull(text: string): string | null {
  const t = text.trim();
  return t === '' ? null : t;
}

export function WriteError({ error }: { error: unknown }) {
  if (error == null) return null;
  return (
    <p role="alert" data-testid="crm-write-error" className="text-xs text-red-600 dark:text-red-400">
      {isCrmConflictError(error)
        ? `This record changed since you opened it and has been reloaded (${crmErrorMessage(error)}). Your text is still here: check it and save again.`
        : `Could not save: ${crmErrorMessage(error)}`}
    </p>
  );
}

/** A labelled inline form with Save and Cancel. */
function EditForm({
  title,
  onSubmit,
  onCancel,
  canSave,
  pending,
  error,
  saveLabel = 'Save',
  children,
  testId,
}: {
  title: string;
  onSubmit: () => void;
  onCancel: () => void;
  canSave: boolean;
  pending: boolean;
  error: unknown;
  saveLabel?: string;
  children: ReactNode;
  testId: string;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSave && !pending) onSubmit();
  };
  return (
    <form
      onSubmit={submit}
      aria-label={title}
      data-testid={testId}
      className="space-y-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</p>
      <div className="flex flex-wrap items-end gap-3">{children}</div>
      <WriteError error={error} />
      <div className="flex gap-2">
        <button type="submit" className={PRIMARY_BUTTON} disabled={!canSave || pending}>
          {pending ? 'Saving…' : saveLabel}
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A button that opens an editor in place; the editor closes itself when its write lands. */
export function Toggle({
  label,
  children,
}: {
  label: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <button type="button" className={LINK_BUTTON} onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  return <div className="basis-full">{children(() => setOpen(false))}</div>;
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  wide = false,
  required = false,
  placeholder,
  list,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'email';
  wide?: boolean;
  required?: boolean;
  placeholder?: string;
  list?: string;
}) {
  return (
    <label className={`${LABEL_CLASSES} ${wide ? 'min-w-[16rem] flex-1' : ''}`}>
      {label}
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        list={list}
        onChange={(e) => onChange(e.target.value)}
        className={CONTROL_CLASSES}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className={`${LABEL_CLASSES} basis-full`}>
      {label}
      <textarea
        value={value}
        rows={4}
        onChange={(e) => onChange(e.target.value)}
        className={`${CONTROL_CLASSES} w-full`}
      />
    </label>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className={LABEL_CLASSES}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value as T)} className={CONTROL_CLASSES}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const asOptions = <T extends string>(values: readonly T[]) => values.map((v) => ({ value: v, label: v }));

// MARK: Account fields

export function StageEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [stage, setStage] = useState<CrmStage>(account.stage === 'closed' ? 'conversation' : account.stage);
  const [stageNote, setStageNote] = useState(account.stageNote ?? '');
  const [note, setNote] = useState('');
  const write = useCrmWrite(
    () => api.setCrmStage(account.id, { stage, stageNote: orNull(stageNote), note: note.trim() }),
    onClose
  );
  return (
    <EditForm
      title="Change stage"
      testId="crm-stage-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={note.trim() !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <SelectField label="Stage" value={stage} options={asOptions(SETTABLE_STAGES)} onChange={setStage} />
      <TextField label="Stage note" value={stageNote} onChange={setStageNote} placeholder="e.g. ambassador" />
      <TextField
        label="What happened (recorded in the history)"
        value={note}
        onChange={setNote}
        wide
        required
      />
    </EditForm>
  );
}

export function CloseAccountEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const write = useCrmWrite(
    () =>
      api.closeCrmAccount(account.id, {
        closedReason: reason.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onClose
  );
  return (
    <EditForm
      title="Close account"
      testId="crm-close-editor"
      saveLabel="Close account"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={reason.trim() !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Why it closed" value={reason} onChange={setReason} wide required />
      <TextField label="History note (optional)" value={note} onChange={setNote} wide />
    </EditForm>
  );
}

export function SegmentEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [segment, setSegment] = useState<CrmSegment>(account.segment);
  const write = useCrmWrite(() => api.setCrmSegment(account.id, segment), onClose);
  return (
    <EditForm
      title="Change segment"
      testId="crm-segment-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={segment !== account.segment}
      pending={write.isPending}
      error={write.error}
    >
      <SelectField label="Segment" value={segment} options={asOptions(CRM_SEGMENTS)} onChange={setSegment} />
    </EditForm>
  );
}

/** A7Y-5203: the select's value for "not ranked" (P8 by the dashboard's rule). */
const NOT_RANKED = 'none';
type RankChoice = CrmPriority | typeof NOT_RANKED;
const RANK_OPTIONS: readonly { value: RankChoice; label: string }[] = [
  ...CRM_PRIORITIES.map((p) => ({ value: p as RankChoice, label: rankLabel(p) })),
  { value: NOT_RANKED, label: 'Not ranked' },
];

export function PriorityEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const current: RankChoice = account.priority ?? NOT_RANKED;
  const [rank, setRank] = useState<RankChoice>(current);
  const write = useCrmWrite(
    () => api.setCrmPriority(account.id, rank === NOT_RANKED ? null : rank),
    onClose
  );
  return (
    <EditForm
      title="Change rank"
      testId="crm-priority-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={rank !== current}
      pending={write.isPending}
      error={write.error}
    >
      <SelectField label="Rank" value={rank} options={RANK_OPTIONS} onChange={setRank} />
    </EditForm>
  );
}

export function NextActionEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [text, setText] = useState(account.nextAction ?? '');
  const write = useCrmWrite(() => api.setCrmNextAction(account.id, orNull(text)), onClose);
  return (
    <EditForm
      title="Next action"
      testId="crm-next-action-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={orNull(text) !== account.nextAction}
      pending={write.isPending}
      error={write.error}
    >
      <TextArea label="Next action (empty clears it)" value={text} onChange={setText} />
    </EditForm>
  );
}

export function LastTouchEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [date, setDate] = useState(account.lastTouch ?? isoDate(new Date()));
  const [note, setNote] = useState(account.lastTouchNote ?? '');
  const write = useCrmWrite(
    () => api.setCrmLastTouch(account.id, { lastTouch: date, lastTouchNote: orNull(note) }),
    onClose
  );
  return (
    <EditForm
      title="Last touch"
      testId="crm-last-touch-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={date !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Date" type="date" value={date} onChange={setDate} required />
      <TextField label="Note" value={note} onChange={setNote} wide />
    </EditForm>
  );
}

export function WatchEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const [watch, setWatch] = useState(account.watch ?? '');
  const write = useCrmWrite(() => api.setCrmWatch(account.id, orNull(watch)), onClose);
  return (
    <EditForm
      title="Watch"
      testId="crm-watch-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={orNull(watch) !== account.watch}
      pending={write.isPending}
      error={write.error}
    >
      <TextField
        label="Watched condition (empty clears it)"
        value={watch}
        onChange={setWatch}
        wide
        placeholder="e.g. their Q4 budget review"
      />
    </EditForm>
  );
}

/** Rename and delete: super_admin only in the API; the caller renders this only for one. */
export function SuperAdminAccountControls({ account }: { account: CrmAccountDoc }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const remove = useCrmWrite(
    () => api.deleteCrmAccount(account.id),
    () => navigate('/admin/cmo/crm')
  );
  return (
    <div data-testid="crm-superadmin-controls" className="flex flex-wrap items-start gap-3">
      <Toggle label="Rename (change id)">
        {(close) => <RenameEditor account={account} onClose={close} />}
      </Toggle>
      {confirming ? (
        <div className="flex basis-full flex-wrap items-center gap-2">
          <span className="text-sm text-red-700 dark:text-red-400">
            Delete {splitAccountName(account.name).name} with its contacts, history and roster items?
          </span>
          <button
            type="button"
            className={DANGER_BUTTON}
            disabled={remove.isPending}
            onClick={() => remove.mutate(undefined)}
          >
            {remove.isPending ? 'Deleting…' : 'Delete for good'}
          </button>
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setConfirming(false)}>
            Keep it
          </button>
          <WriteError error={remove.error} />
        </div>
      ) : (
        <button type="button" className={`${LINK_BUTTON} text-red-600`} onClick={() => setConfirming(true)}>
          Delete account
        </button>
      )}
    </div>
  );
}

function RenameEditor({ account, onClose }: { account: CrmAccountDoc; onClose: () => void }) {
  const navigate = useNavigate();
  const [newId, setNewId] = useState(account.id);
  const write = useCrmWrite(
    () => api.renameCrmAccount(account.id, newId.trim()),
    (result) => {
      onClose();
      navigate(accountPagePath(result.account.id), { replace: true });
    }
  );
  return (
    <EditForm
      title="Rename"
      testId="crm-rename-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={newId.trim() !== '' && newId.trim() !== account.id}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="New id (acct-…); the old one keeps working" value={newId} onChange={setNewId} wide />
    </EditForm>
  );
}

// MARK: Contacts

interface ContactDraft {
  name: string;
  role: string;
  email: string;
  note: string;
  reportsTo: string;
}

const draftOf = (c: CrmContactDoc | null): ContactDraft => ({
  name: c?.name ?? '',
  role: c?.role ?? '',
  email: c?.email ?? '',
  note: c?.note ?? '',
  reportsTo: c?.reportsTo ?? '',
});

/** Adds a contact to the account, or edits one (the upsert replaces the whole contact). */
export function ContactEditor({
  accountId,
  contact,
  contacts,
  onClose,
}: {
  accountId: string;
  contact: CrmContactDoc | null;
  contacts: readonly CrmContactDoc[];
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ContactDraft>(draftOf(contact));
  const [primary, setPrimary] = useState(contact?.primary ?? false);
  const set = (key: keyof ContactDraft) => (v: string) => setDraft({ ...draft, [key]: v });
  const write = useCrmWrite(
    () =>
      api.upsertCrmContact({
        ...(contact ? { id: contact.id } : {}),
        accountId,
        name: draft.name.trim(),
        role: orNull(draft.role),
        email: orNull(draft.email),
        note: orNull(draft.note),
        reportsTo: orNull(draft.reportsTo),
        departed: contact?.departed ?? false,
        primary,
      }),
    onClose
  );
  const managers = contacts.filter((c) => c.id !== contact?.id);
  return (
    <EditForm
      title={contact ? `Edit ${contact.name}` : 'Add contact'}
      testId="crm-contact-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={draft.name.trim() !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Name" value={draft.name} onChange={set('name')} required />
      <TextField label="Role" value={draft.role} onChange={set('role')} />
      <TextField label="Email" type="email" value={draft.email} onChange={set('email')} />
      <SelectField
        label="Reports to"
        value={draft.reportsTo}
        options={[{ value: '', label: 'Nobody' }, ...managers.map((c) => ({ value: c.id, label: c.name }))]}
        onChange={set('reportsTo')}
      />
      <TextField label="Note" value={draft.note} onChange={set('note')} wide />
      {contact === null ? (
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
          Primary contact
        </label>
      ) : null}
    </EditForm>
  );
}

export function ContactActions({
  accountId,
  contact,
  contacts,
  isSuperAdmin,
}: {
  accountId: string;
  contact: CrmContactDoc;
  contacts: readonly CrmContactDoc[];
  isSuperAdmin: boolean;
}) {
  const departed = useCrmWrite(() => api.setCrmContactDeparted(contact.id, !contact.departed));
  const primary = useCrmWrite(() => api.setCrmContactPrimary(contact.id));
  const remove = useCrmWrite(() => api.deleteCrmContact(contact.id));
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Toggle label="Edit">
        {(close) => (
          <ContactEditor accountId={accountId} contact={contact} contacts={contacts} onClose={close} />
        )}
      </Toggle>
      {!contact.primary && !contact.departed ? (
        <button type="button" className={LINK_BUTTON} onClick={() => primary.mutate(undefined)}>
          Make primary
        </button>
      ) : null}
      <button type="button" className={LINK_BUTTON} onClick={() => departed.mutate(undefined)}>
        {contact.departed ? 'Mark as current' : 'Mark as departed'}
      </button>
      {isSuperAdmin ? (
        <button
          type="button"
          className={`${LINK_BUTTON} text-red-600`}
          onClick={() => remove.mutate(undefined)}
        >
          Delete contact
        </button>
      ) : null}
      <WriteError error={departed.error ?? primary.error ?? remove.error} />
    </div>
  );
}

// MARK: History

export function HistoryLineEditor({
  accountId,
  sources,
  onClose,
}: {
  accountId: string;
  sources: readonly string[];
  onClose: () => void;
}) {
  const [date, setDate] = useState(isoDate(new Date()));
  const [source, setSource] = useState('admin');
  const [channel, setChannel] = useState('');
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  // One id per draft: a retried save of the same line cannot write it twice.
  const [requestId] = useState(() => crypto.randomUUID());
  const write = useCrmWrite(
    () =>
      api.appendCrmEvent({
        accountId,
        date,
        source: source.trim(),
        channel: orNull(channel),
        author: orNull(author),
        text: text.trim(),
        requestId,
      }),
    onClose
  );
  return (
    <EditForm
      title="Add a history line"
      testId="crm-history-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={date !== '' && source.trim() !== '' && text.trim() !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Date" type="date" value={date} onChange={setDate} required />
      <TextField label="Source" value={source} onChange={setSource} list="crm-sources" required />
      <datalist id="crm-sources">
        {sources.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <TextField label="Channel" value={channel} onChange={setChannel} placeholder="email, call…" />
      <TextField label="Author" value={author} onChange={setAuthor} />
      <TextArea label="What happened" value={text} onChange={setText} />
    </EditForm>
  );
}

export function DeleteEventButton({ eventId }: { eventId: string }) {
  const remove = useCrmWrite(() => api.deleteCrmEvent(eventId));
  return (
    <>
      <button type="button" className={`${LINK_BUTTON} text-red-600`} onClick={() => remove.mutate(undefined)}>
        Delete line
      </button>
      <WriteError error={remove.error} />
    </>
  );
}

// MARK: Roster items

export function RosterItemEditor({ accountId, onClose }: { accountId: string | null; onClose: () => void }) {
  const [kind, setKind] = useState<CrmRosterItemKind>('touch');
  const [title, setTitle] = useState('');
  const [proposal, setProposal] = useState('');
  const [due, setDue] = useState(isoDate(new Date()));
  const [block, setBlock] = useState<CrmBlock>('contact');
  const [where, setWhere] = useState('');
  const write = useCrmWrite(
    () =>
      api.addCrmRosterItem({
        kind,
        accountId,
        title: title.trim(),
        proposal: orNull(proposal),
        due,
        block,
        where: orNull(where),
      }),
    onClose
  );
  return (
    <EditForm
      title="Add a roster item"
      testId="crm-roster-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={title.trim() !== '' && due !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <SelectField label="Kind" value={kind} options={asOptions(CRM_ROSTER_ITEM_KINDS)} onChange={setKind} />
      <TextField label="Who / what" value={title} onChange={setTitle} wide required />
      {/* A7Y-5084: a suggestion is kept out of the title, which later readers take as fact. */}
      <TextField label="Proposal (a suggestion, not a fact)" value={proposal} onChange={setProposal} wide />
      <TextField label="Due" type="date" value={due} onChange={setDue} required />
      <SelectField label="Block" value={block} options={asOptions(CRM_BLOCKS)} onChange={setBlock} />
      <TextField label="Where (link)" value={where} onChange={setWhere} wide />
    </EditForm>
  );
}

export function RedateEditor({ item, onClose }: { item: CrmRosterItemDoc; onClose: () => void }) {
  const [due, setDue] = useState(item.due);
  const write = useCrmWrite(() => api.redateCrmRosterItem(item.id, due), onClose);
  return (
    <EditForm
      title="Re-date"
      testId="crm-redate-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={due !== '' && due !== item.due}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="New due date" type="date" value={due} onChange={setDue} required />
    </EditForm>
  );
}

export function CloseItemEditor({ item, onClose }: { item: CrmRosterItemDoc; onClose: () => void }) {
  const [route, setRoute] = useState('');
  const [outcome, setOutcome] = useState('');
  const [closedOn, setClosedOn] = useState(isoDate(new Date()));
  const write = useCrmWrite(
    () => api.closeCrmRosterItem(item.id, { route: route.trim(), outcome: outcome.trim(), closedOn }),
    onClose
  );
  return (
    <EditForm
      title="Close item"
      testId="crm-close-item-editor"
      saveLabel="Close item"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={route.trim() !== '' && outcome.trim() !== '' && closedOn !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Route" value={route} onChange={setRoute} placeholder="email, call…" required />
      <TextField label="Outcome" value={outcome} onChange={setOutcome} wide required />
      <TextField label="Closed on" type="date" value={closedOn} onChange={setClosedOn} required />
    </EditForm>
  );
}

export function LinkItemEditor({
  item,
  accounts,
  onClose,
}: {
  item: CrmRosterItemDoc;
  accounts: readonly CrmAccountListItem[];
  onClose: () => void;
}) {
  const sorted = [...accounts].sort((a, b) => a.account.name.localeCompare(b.account.name));
  const [accountId, setAccountId] = useState('');
  const write = useCrmWrite(() => api.linkCrmRosterItem(item.id, accountId), onClose);
  return (
    <EditForm
      title="Link to an account"
      testId="crm-link-editor"
      saveLabel="Link"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={accountId !== ''}
      pending={write.isPending}
      error={write.error}
    >
      <SelectField
        label="Account"
        value={accountId}
        options={[
          { value: '', label: 'Choose an account' },
          ...sorted.map((a) => ({ value: a.account.id, label: splitAccountName(a.account.name).name })),
        ]}
        onChange={setAccountId}
      />
    </EditForm>
  );
}

/** Re-date and close for an open item; delete for a super_admin. Shared by the account page and this-week view. */
export function RosterItemActions({ item, isSuperAdmin }: { item: CrmRosterItemDoc; isSuperAdmin: boolean }) {
  const remove = useCrmWrite(() => api.deleteCrmRosterItem(item.id));
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Toggle label="Re-date">{(close) => <RedateEditor item={item} onClose={close} />}</Toggle>
      <Toggle label="Close">{(close) => <CloseItemEditor item={item} onClose={close} />}</Toggle>
      {isSuperAdmin ? (
        <button type="button" className={`${LINK_BUTTON} text-red-600`} onClick={() => remove.mutate(undefined)}>
          Delete item
        </button>
      ) : null}
      <WriteError error={remove.error} />
    </div>
  );
}

// MARK: New account

/**
 * Add-account form: the account, then its first contact and first history line
 * when given. Three writes, in that order; on success the new account opens.
 */
export function AddAccountForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [segment, setSegment] = useState<CrmSegment>('mid');
  const [stage, setStage] = useState<CrmStage>('lead');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [historyDate, setHistoryDate] = useState(isoDate(new Date()));
  const [historyText, setHistoryText] = useState('');
  const [requestId] = useState(() => crypto.randomUUID());
  // Once the account or its contact exists, a retry must not create it again.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [contactAdded, setContactAdded] = useState(false);

  const write = useCrmWrite(
    async () => {
      const accountId =
        createdId ??
        (await api.createCrmAccount({ name: name.trim(), segment, stage })).account.id;
      setCreatedId(accountId);
      if (contactName.trim() && !contactAdded) {
        await api.upsertCrmContact({
          accountId,
          name: contactName.trim(),
          role: orNull(contactRole),
          primary: true,
        });
        setContactAdded(true);
      }
      if (historyText.trim())
        await api.appendCrmEvent({
          accountId,
          date: historyDate,
          source: 'admin',
          text: historyText.trim(),
          requestId,
        });
      return accountId;
    },
    (accountId) => {
      onClose();
      navigate(accountPagePath(accountId));
    }
  );
  return (
    <EditForm
      title="Add account"
      testId="crm-add-account"
      saveLabel="Add account"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={name.trim() !== '' && (historyText.trim() === '' || historyDate !== '')}
      pending={write.isPending}
      error={write.error}
    >
      <TextField label="Name" value={name} onChange={setName} required wide />
      <SelectField label="Segment" value={segment} options={asOptions(CRM_SEGMENTS)} onChange={setSegment} />
      <SelectField label="Stage" value={stage} options={asOptions(SETTABLE_STAGES)} onChange={setStage} />
      <TextField label="First contact" value={contactName} onChange={setContactName} />
      <TextField label="Their role" value={contactRole} onChange={setContactRole} />
      <TextField label="First history line: date" type="date" value={historyDate} onChange={setHistoryDate} />
      <TextArea label="First history line" value={historyText} onChange={setHistoryText} />
      {createdId !== null ? (
        <p className="basis-full text-xs text-zinc-600 dark:text-zinc-400">
          The account was created as <code>{createdId}</code>; saving again adds only what is missing.
        </p>
      ) : null}
    </EditForm>
  );
}

// MARK: Slots, parked and the week plan (A7Y-5210)

/** One row of a list editor, with a Remove control. */
function ListRow({ children, onRemove, label }: { children: ReactNode; onRemove: () => void; label: string }) {
  return (
    <div className="flex basis-full flex-wrap items-end gap-3" data-testid="crm-list-row">
      {children}
      <button type="button" className={LINK_BUTTON} onClick={onRemove} aria-label={label}>
        Remove
      </button>
    </div>
  );
}

function accountOptions(accounts: readonly CrmAccountListItem[], none: string) {
  return [
    { value: '', label: none },
    ...[...accounts]
      .sort((a, b) => a.account.name.localeCompare(b.account.name))
      .map((a) => ({ value: a.account.id, label: splitAccountName(a.account.name).name })),
  ];
}

/** The ten conversation slots, replaced whole: a slot with no account and no reason is left out. */
export function SlotsEditor({
  slots,
  accounts,
  onClose,
}: {
  slots: readonly CrmSlotDoc[];
  accounts: readonly CrmAccountListItem[];
  onClose: () => void;
}) {
  const bySlot = new Map(slots.map((s) => [s.slot, s] as const));
  const [rows, setRows] = useState(() =>
    Array.from({ length: CRM_SLOT_CAP }, (_, i) => {
      const slot = bySlot.get(i + 1);
      return { slot: i + 1, accountId: slot?.accountId ?? '', why: slot?.why ?? '' };
    })
  );
  const set = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const write = useCrmWrite(
    () =>
      api.replaceCrmSlots(
        rows
          .filter((r) => r.accountId !== '' || r.why.trim() !== '')
          .map((r) => ({ slot: r.slot, accountId: orNull(r.accountId), why: orNull(r.why) }))
      ),
    onClose
  );
  const options = accountOptions(accounts, 'Open');
  return (
    <EditForm
      title="Conversation slots"
      testId="crm-slots-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave
      pending={write.isPending}
      error={write.error}
    >
      {rows.map((r, i) => (
        <div key={r.slot} className="flex basis-full flex-wrap items-end gap-3" data-testid="crm-slot-row">
          <span className="pb-2 tabular-nums text-sm text-zinc-500">{r.slot}.</span>
          <SelectField label="Account" value={r.accountId} options={options} onChange={(v) => set(i, { accountId: v })} />
          <TextField label="Why it holds a slot" value={r.why} onChange={(v) => set(i, { why: v })} wide />
        </div>
      ))}
    </EditForm>
  );
}

/** The parked list, replaced whole. */
export function ParkedEditor({
  parked,
  accounts,
  onClose,
}: {
  parked: readonly CrmParkedEntry[];
  accounts: readonly CrmAccountListItem[];
  onClose: () => void;
}) {
  const [rows, setRows] = useState(() => parked.map((p) => ({ accountId: p.accountId, why: p.why ?? '' })));
  const set = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const write = useCrmWrite(
    () => api.replaceCrmParked(rows.map((r) => ({ accountId: r.accountId, why: orNull(r.why) }))),
    onClose
  );
  const ids = rows.map((r) => r.accountId);
  const complete = rows.every((r) => r.accountId !== '') && new Set(ids).size === ids.length;
  const options = accountOptions(accounts, 'Choose an account');
  return (
    <EditForm
      title="Parked"
      testId="crm-parked-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={complete}
      pending={write.isPending}
      error={write.error}
    >
      {rows.map((r, i) => (
        <ListRow key={i} onRemove={() => setRows(rows.filter((_, n) => n !== i))} label={`Remove parked entry ${i + 1}`}>
          <SelectField label="Account" value={r.accountId} options={options} onChange={(v) => set(i, { accountId: v })} />
          <TextField label="Why" value={r.why} onChange={(v) => set(i, { why: v })} wide />
        </ListRow>
      ))}
      <button type="button" className={LINK_BUTTON} onClick={() => setRows([...rows, { accountId: '', why: '' }])}>
        Add a parked account
      </button>
    </EditForm>
  );
}

/** A week's plan (`weekStart` is its Monday), replaced whole: a note and the moves, each with an owner and a day. */
export function WeekPlanEditor({
  weekStart,
  plan,
  accounts,
  onClose,
}: {
  weekStart: string;
  plan: CrmWeekPlanDoc | null;
  accounts: readonly CrmAccountListItem[];
  onClose: () => void;
}) {
  const [note, setNote] = useState(plan?.note ?? '');
  const [moves, setMoves] = useState(() =>
    (plan?.moves ?? []).map((m) => ({ move: m.move, accountId: m.accountId ?? '', owner: m.owner, day: m.day }))
  );
  const set = (i: number, patch: Partial<(typeof moves)[number]>) =>
    setMoves(moves.map((m, n) => (n === i ? { ...m, ...patch } : m)));
  const write = useCrmWrite(
    () =>
      api.writeCrmWeekPlan(weekStart, {
        note: orNull(note),
        moves: moves.map((m) => ({
          move: m.move.trim(),
          accountId: orNull(m.accountId),
          owner: m.owner.trim(),
          day: m.day.trim(),
        })),
      }),
    onClose
  );
  const complete = moves.every((m) => m.move.trim() !== '' && m.owner.trim() !== '' && m.day.trim() !== '');
  const options = accountOptions(accounts, 'No account');
  return (
    <EditForm
      title={`Week plan · ${weekStart}`}
      testId="crm-week-plan-editor"
      onSubmit={() => write.mutate(undefined)}
      onCancel={onClose}
      canSave={complete}
      pending={write.isPending}
      error={write.error}
    >
      <TextArea label="Note" value={note} onChange={setNote} />
      {moves.map((m, i) => (
        <ListRow key={i} onRemove={() => setMoves(moves.filter((_, n) => n !== i))} label={`Remove move ${i + 1}`}>
          <TextField label={`Move ${i + 1}`} value={m.move} onChange={(v) => set(i, { move: v })} wide required />
          <SelectField label="Account" value={m.accountId} options={options} onChange={(v) => set(i, { accountId: v })} />
          <TextField label="Owner" value={m.owner} onChange={(v) => set(i, { owner: v })} required />
          <TextField label="Day" value={m.day} onChange={(v) => set(i, { day: v })} placeholder="Mon" required />
        </ListRow>
      ))}
      <button
        type="button"
        className={LINK_BUTTON}
        onClick={() => setMoves([...moves, { move: '', accountId: '', owner: '', day: '' }])}
      >
        Add a move
      </button>
    </EditForm>
  );
}
